import * as React from "react";
import { Helmet } from "react-helmet-async";
import { Route, Switch, useLocation } from "react-router-dom";

// components
import CheckoutLoading from "./components/Checkout/Loading";
import Footer from "./components/Footer";
import AwinScript from "./services/AwinScript";
import ScrollProvider from "./services/Scroll";

// pages
import { pathOf } from "../shared/routes/index.js";
import CanonicalRedirect from "./components/CanonicalRedirect";
import CanonicalUrl from "./components/CanonicalUrl";
import Navbar from "./components/Navbar";
import NavbarFallback from "./components/Navbar/NavbarFallback";
import PageMain from "./components/PageMain";
import SignInRedirect from "./components/SignInRedirect";
import { showsFooter } from "./domain/authPages";
import { SIGNED_IN_PATHS } from "./domain/signInRedirect";
import { ErrorBoundary } from "./errors";
import { useCurrentUser } from "./globalState";
import { isFooterVisible } from "./navigation/footer";
import Audio from "./pages/Audio";
import BrowseProducts from "./pages/BrowseProducts";
import BrowseShops from "./pages/BrowseShops";
import Contact from "./pages/Contact";
import Home from "./pages/Home";
import Logout from "./pages/Logout";
import PolicyPage from "./pages/Policy/PolicyPage";
import ShopV2 from "./pages/ShopV2";
import Video from "./pages/Video";
import WheresMyOrder from "./pages/WheresMyOrder";
import EmailModalProvider from "./services/EmailModal";
import { LoadingUI } from "./theme";

// Lazy pages
const SigningStudio = React.lazy(() => import("./pages/SigningStudio"));
const CameoLandingPage = React.lazy(() =>
  import("./pages/ShopV2/CameoLandingPage"),
);
const Checkout = React.lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = React.lazy(() => import("./pages/Checkout/Success"));
const DashboardRouter = React.lazy(() => import("./dashboard/DashboardRouter"));
const AutographsMadeSimple = React.lazy(() =>
  import("./pages/AutographsMadeSimple"),
);
const ProductCharacterAssignment = React.lazy(() =>
  import("./pages/ProductCharacterAssignment"),
);
const COA = React.lazy(() => import("./pages/COA"));
const Snapshot = React.lazy(() => import("./pages/Snapshot"));
const OnboardingForm = React.lazy(() => import("./pages/OnboardingForm/"));
const Pending = React.lazy(() => import("./pages/OnboardingForm/Pending"));
const Testimonials = React.lazy(() => import("./pages/Testimonials"));
const Setup = React.lazy(() => import("./pages/Setup"));
const ReuploadImage = React.lazy(() => import("./pages/ReuploadImage"));
const LoginPage = React.lazy(() => import("./pages/Auth/LoginPage"));
const ResetPasswordPage = React.lazy(() =>
  import("./pages/Auth/ResetPasswordPage"),
);
const ChangePasswordPage = React.lazy(() =>
  import("./pages/Auth/ChangePasswordPage"),
);
const UserInvitePage = React.lazy(() => import("./pages/Auth/UserInvitePage"));
const TalentInvitePage = React.lazy(() =>
  import("./pages/Auth/TalentInvitePage"),
);
const PrintBoxRedirect = React.lazy(() =>
  import("./pages/Auth/PrintBoxRedirect"),
);
const VerifyPage = React.lazy(() => import("./pages/Auth/VerifyPage"));
const SecureAccountPage = React.lazy(() =>
  import("./pages/Auth/SecureAccountPage"),
);

/* eslint-disable complexity */
export default function Routes() {
  const location = useLocation();
  const currentUser = useCurrentUser();
  const pageResetKeys = [location.pathname];

  if (location.pathname === "/checkout") {
    return (
      <ErrorBoundary section="page" resetKeys={pageResetKeys}>
        <React.Suspense fallback={<CheckoutLoading />}>
          <Helmet>
            <title>Checkout | Streamily</title>
            <meta name="description" content="Checkout | Streamily" />
          </Helmet>
          <Checkout />
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname === "/pca" && currentUser.isAdmin) {
    return (
      <ErrorBoundary section="page" resetKeys={pageResetKeys}>
        <React.Suspense fallback={<LoadingUI message="Loading..." />}>
          <React.Fragment>
            <Helmet>
              <title>Product Character Assignment | Streamily</title>
              <meta
                name="description"
                content="Product Character Assignment | Streamily"
              />
            </Helmet>
            <ProductCharacterAssignment />
          </React.Fragment>
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname.startsWith("/cameo/")) {
    return (
      <ErrorBoundary section="page" resetKeys={pageResetKeys}>
        <React.Suspense fallback={<LoadingUI message="Loading..." />}>
          <CameoLandingPage />
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname.startsWith("/setup")) {
    return (
      <ErrorBoundary section="page" resetKeys={pageResetKeys}>
        <React.Suspense fallback={<LoadingUI message="Loading..." />}>
          <Setup />
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ScrollProvider>
      <AwinScript />
      <EmailModalProvider>
        <ErrorBoundary
          section="navbar"
          fallback={<NavbarFallback />}
          resetKeys={pageResetKeys}
        >
          <Navbar />
        </ErrorBoundary>
        <CanonicalUrl />
        <CanonicalRedirect>
          <PageMain fallback={<LoadingUI message="Loading..." />}>
            <ErrorBoundary section="page" resetKeys={pageResetKeys}>
              <Switch>
                <Route
                  path="/checkout/confirmed"
                  render={(props) => (
                    <React.Suspense fallback={<CheckoutLoading />}>
                      <Helmet>
                        <title>Order Confirmed | Streamily</title>
                        <meta
                          name="description"
                          content="Order Confirmed | Streamily"
                        />
                      </Helmet>
                      <CheckoutSuccess {...props} />
                    </React.Suspense>
                  )}
                />
                <Route
                  path="/reupload-image"
                  render={() => (
                    <React.Suspense
                      fallback={<LoadingUI message="Loading..." />}
                    >
                      <ReuploadImage />
                    </React.Suspense>
                  )}
                />
                <Route
                  path="/products"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Products</title>
                        <meta
                          name="description"
                          content="Streamily | Products"
                        />
                      </Helmet>

                      <BrowseProducts {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/shops"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Shops</title>
                        <meta name="description" content="Streamily | Shops" />
                      </Helmet>

                      <BrowseShops {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/contact"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Contact</title>
                        <meta
                          name="description"
                          content="Streamily | Contact"
                        />
                      </Helmet>
                      <Contact {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/policy"
                  render={() => <PolicyPage slug="policy" />}
                />
                <Route
                  path="/terms-and-conditions"
                  render={() => <PolicyPage slug="terms-and-conditions" />}
                />
                <Route
                  path="/return-policy"
                  render={() => <PolicyPage slug="return-policy" />}
                />
                <Route
                  path="/privacy-policy"
                  render={() => <PolicyPage slug="privacy-policy" />}
                />
                <Route
                  path="/privacy"
                  render={() => <PolicyPage slug="privacy-policy" />}
                />
                <Route
                  path="/talent-terms-of-service"
                  render={() => <PolicyPage slug="talent-terms-of-service" />}
                />
                <Route
                  path="/coa"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Certificate of Authenticity</title>
                        <meta
                          name="description"
                          content="Streamily | Certificate of Authenticity"
                        />
                      </Helmet>
                      <COA {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/talent/signup"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Onboarding Form</title>
                        <meta
                          name="description"
                          content="Streamily | Onboarding Form"
                        />
                      </Helmet>
                      <OnboardingForm {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/talent/pending"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Pending Applications</title>
                        <meta
                          name="description"
                          content="Streamily | Pending Applications"
                        />
                      </Helmet>
                      <Pending {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/testimonials"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Talent Testimonials</title>
                        <meta
                          name="description"
                          content="Streamily | Talent Testimonials"
                        />
                      </Helmet>
                      <Testimonials {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/video"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Video</title>
                        <meta name="description" content="Streamily | Video" />
                      </Helmet>
                      <Video {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/audio"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Audio</title>
                        <meta name="description" content="Streamily | Audio" />
                      </Helmet>
                      <Audio {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/snapshot"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Snapshot</title>
                        <meta
                          name="description"
                          content="Streamily | Snapshot"
                        />
                      </Helmet>
                      <Snapshot {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/wheres-my-order"
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Where’s My Order?</title>
                        <meta
                          name="description"
                          content="Streamily | Where’s My Order?"
                        />
                      </Helmet>
                      <WheresMyOrder {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path={pathOf("autographs-made-simple")}
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily | Autographs Made Simple</title>
                        <meta
                          name="description"
                          content="Streamily | Autographs Made Simple"
                        />
                      </Helmet>
                      <AutographsMadeSimple {...props} />
                    </React.Fragment>
                  )}
                />
                <Route
                  path="/logout"
                  render={(props) => <Logout {...props} />}
                />
                <Route
                  exact
                  path="/login"
                  render={() => <LoginPage mode="login" />}
                />
                <Route
                  exact
                  path="/join"
                  render={() => <LoginPage mode="create" />}
                />
                <Route
                  exact
                  path={pathOf("auth.reset-password")}
                  component={ResetPasswordPage}
                />
                <Route
                  exact
                  path={pathOf("auth.change-password")}
                  component={ChangePasswordPage}
                />
                <Route
                  exact
                  path={pathOf("auth.user-invite")}
                  component={UserInvitePage}
                />
                <Route
                  exact
                  path={pathOf("auth.talent-invite")}
                  component={TalentInvitePage}
                />
                <Route
                  exact
                  path="/p/:printBoxId"
                  component={PrintBoxRedirect}
                />
                <Route
                  exact
                  path={pathOf("auth.verify")}
                  component={VerifyPage}
                />
                <Route
                  exact
                  path={pathOf("auth.secure-account")}
                  component={SecureAccountPage}
                />
                {!currentUser.isLoggedIn && (
                  <Route path={SIGNED_IN_PATHS} component={SignInRedirect} />
                )}
                {currentUser.isLoggedIn && (
                  <Route
                    path={pathOf("dashboard.start")}
                    component={DashboardRouter}
                  />
                )}
                <Route
                  path="/signing-studio"
                  render={() => (
                    <React.Suspense
                      fallback={<LoadingUI message="Loading..." />}
                    >
                      <SigningStudio />
                    </React.Suspense>
                  )}
                />
                <Route path="/:route/:productId?" component={ShopV2} />
                <Route
                  render={(props) => (
                    <React.Fragment>
                      <Helmet>
                        <title>Streamily - Livestream Autographs</title>
                        <meta
                          name="description"
                          content="Get livestream autographs from all your favorite celebrities signed LIVE on Instagram, YouTube, Twitch, and TikTok delivered straight to your door."
                        />
                      </Helmet>
                      <Home {...props} />
                    </React.Fragment>
                  )}
                />
              </Switch>
            </ErrorBoundary>
          </PageMain>
        </CanonicalRedirect>

        {showsFooter(location.pathname) &&
          isFooterVisible(location.pathname) && (
            <ErrorBoundary
              section="footer"
              fallback={null}
              resetKeys={pageResetKeys}
            >
              <Footer />
            </ErrorBoundary>
          )}
      </EmailModalProvider>
    </ScrollProvider>
  );
}
